import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2, Tags } from "lucide-react";
import { toast } from "sonner";

export default function Categorias() {
  const { user } = useAuth();
  const isAdmin = ["SUPER_ADMIN", "MASTER"].includes(user.role);
  const [cats, setCats] = useState(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ nome: "", ativo: true });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/categorias").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);

  const open = (c) => { c ? (setForm({ nome: c.nome, ativo: c.ativo }), setEditId(c.id)) : (setForm({ nome: "", ativo: true }), setEditId(null)); setModal(true); };

  const save = async () => {
    if (!form.nome.trim()) return toast.error("Nome obrigatório");
    setSaving(true);
    try {
      if (editId) await api.put(`/categorias/${editId}`, form);
      else await api.post("/categorias", form);
      toast.success(editId ? "Categoria atualizada" : "Categoria criada");
      setModal(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };
  const toggle = async (c) => { try { await api.put(`/categorias/${c.id}`, { nome: c.nome, ativo: !c.ativo }); load(); } catch (e) { toast.error(e.response?.data?.detail || "Erro"); } };
  const del = async (c) => {
    if (!window.confirm(`Excluir a categoria "${c.nome}"? Esta ação não pode ser desfeita.`)) return;
    try { await api.delete(`/categorias/${c.id}`); toast.success("Categoria excluída"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-5" data-testid="categorias-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Cadastros</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase flex items-center gap-2"><Tags className="w-8 h-8 text-primary" /> Categorias</h1>
        </div>
        <Button onClick={() => open(null)} data-testid="btn-nova-categoria"><Plus className="w-4 h-4" /> Nova Categoria</Button>
      </div>

      {!cats ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {cats.map((c) => (
            <Card key={c.id} className="bg-card/70 p-4 flex items-center justify-between gap-2" data-testid={`categoria-${c.nome}`}>
              <div className="min-w-0">
                <div className="font-semibold truncate">{c.nome}</div>
                <div className={`text-[10px] uppercase font-bold ${c.ativo ? "text-emerald-300" : "text-red-300"}`}>{c.ativo ? "Ativa" : "Inativa"}</div>
              </div>
              <div className="flex items-center gap-1">
                <Switch checked={c.ativo} onCheckedChange={() => toggle(c)} data-testid={`cat-toggle-${c.nome}`} />
                <Button variant="ghost" size="icon" onClick={() => open(c)} data-testid={`cat-edit-${c.nome}`}><Pencil className="w-4 h-4" /></Button>
                {isAdmin && <Button variant="ghost" size="icon" className="text-red-400" onClick={() => del(c)} data-testid={`cat-del-${c.nome}`}><Trash2 className="w-4 h-4" /></Button>}
              </div>
            </Card>
          ))}
          {!cats.length && <Card className="p-10 text-center text-muted-foreground sm:col-span-2 lg:col-span-3">Nenhuma categoria.</Card>}
        </div>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Editar" : "Nova"} Categoria</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome</Label><Input value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} data-testid="cat-nome" /></div>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><Switch checked={form.ativo} onCheckedChange={(v) => setForm((f) => ({ ...f, ativo: v }))} data-testid="cat-ativo" /> Ativa</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-salvar-categoria">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
